import _cloneDeep from 'lodash/cloneDeep';
import {
  BaseMetricsRequest,
  CategoryType,
  ContentMetricType,
  GetScoreForContentMetricByCategoryTypeRequest,
  GetScoreForContentMetricRequest,
  TimePeriodRange,
  VoiceAndToneMatchType
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/ContentServerProxy/types';

/**
 * Builder for creating content metrics requests.
 * We should extend this as needed.
 */
export class ContentMetricsRequestBuilder {
  protected request: BaseMetricsRequest;

  protected contentMetricType: ContentMetricType;

  protected categoryType: CategoryType;

  protected voiceAndToneMatchTypes: VoiceAndToneMatchType[];

  constructor(
    baseRequest?: BaseMetricsRequest,
    contentMetricType?: ContentMetricType,
    categoryType?: CategoryType,
    voiceAndToneMatchTypes?: VoiceAndToneMatchType[]
  ) {
    this.request = baseRequest
      ? _cloneDeep(baseRequest)
      : {
          retailerId: null,
          stacklineSkus: [],
          brandIds: [],
          categoryIds: [],
          subcategoryIds: [],
          segmentIds: [],
          businessUnits: [],
          startWeekId: null,
          endWeekId: null,
          resultCount: 12000,
          sortField: ''
        };
    this.contentMetricType = contentMetricType;
    this.categoryType = categoryType;
    this.voiceAndToneMatchTypes = voiceAndToneMatchTypes;
  }

  public setMainEntity(mainEntity: any): ContentMetricsRequestBuilder {
    const { type, id } = mainEntity;

    switch (type) {
      case 'category':
        return this.setCategoryIds([+id]);
      case 'subcategory':
        return this.setSubcategoryIds([+id]);
      case 'brand':
        return this.setBrandIds([+id]);
      case 'product':
        return this.setStacklineSkus([id]);
      case 'segment':
        return this.setSegmentIds([id]);
      case 'businessUnit':
        return this.setBusinessUnits([id]);
      default:
        console.warn(`Unknown entity type: ${type}`);
        return this;
    }
  }

  public setMainTimePeriod(mainTimePeriod: TimePeriodRange): ContentMetricsRequestBuilder {
    this.setStartWeekId(mainTimePeriod.startWeek).setEndWeekId(mainTimePeriod.endWeek);
    return this;
  }

  public setRetailerId(retailerId: number): ContentMetricsRequestBuilder {
    this.request.retailerId = retailerId;
    return this;
  }

  public setStacklineSkus(stacklineSkus: string[]): ContentMetricsRequestBuilder {
    this.request.stacklineSkus = stacklineSkus;
    return this;
  }

  public setBrandIds(brandIds: number[]): ContentMetricsRequestBuilder {
    this.request.brandIds = brandIds;
    return this;
  }

  public setCategoryIds(categoryIds: number[]): ContentMetricsRequestBuilder {
    this.request.categoryIds = categoryIds;
    return this;
  }

  public setSubcategoryIds(subcategoryIds: number[]): ContentMetricsRequestBuilder {
    this.request.subcategoryIds = subcategoryIds;
    return this;
  }

  public setSegmentIds(segmentIds: string[]): ContentMetricsRequestBuilder {
    this.request.segmentIds = segmentIds;
    return this;
  }

  public setBusinessUnits(businessUnits: string[]): ContentMetricsRequestBuilder {
    this.request.businessUnits = businessUnits;
    return this;
  }

  public setSortField(sortField: string): ContentMetricsRequestBuilder {
    this.request.sortField = sortField;
    return this;
  }

  public setStartWeekId(startWeekId: number): ContentMetricsRequestBuilder {
    this.request.startWeekId = startWeekId;
    return this;
  }

  public setEndWeekId(endWeekId: number): ContentMetricsRequestBuilder {
    this.request.endWeekId = endWeekId;
    return this;
  }

  public setOffset(offset: number): ContentMetricsRequestBuilder {
    this.request.offset = offset;
    return this;
  }

  public setContentMetricType(contentMetricType: ContentMetricType): ContentMetricsRequestBuilder {
    this.contentMetricType = contentMetricType;
    return this;
  }

  public setCategoryType(categoryType: CategoryType): ContentMetricsRequestBuilder {
    this.categoryType = categoryType;
    return this;
  }

  public setVoiceAndToneMatchTypes(voiceAndToneMatchTypes: VoiceAndToneMatchType[]): ContentMetricsRequestBuilder {
    this.voiceAndToneMatchTypes = voiceAndToneMatchTypes;
    return this;
  }

  public clone(): ContentMetricsRequestBuilder {
    return new ContentMetricsRequestBuilder(
      _cloneDeep(this.request),
      this.contentMetricType,
      this.categoryType,
      this.voiceAndToneMatchTypes
    );
  }

  public build(): BaseMetricsRequest | GetScoreForContentMetricRequest | GetScoreForContentMetricByCategoryTypeRequest {
    return {
      ...this.request,
      ...(this.contentMetricType ? { contentMetricType: this.contentMetricType } : {}),
      ...(this.categoryType ? { categoryType: this.categoryType } : {}),
      ...(this.voiceAndToneMatchTypes ? { voiceAndToneMatchTypes: this.voiceAndToneMatchTypes } : {})
    };
  }
}

export class GetContentMetricsForProductRequestBuilder {
  private retailerSku: string;

  private stacklineSku: string;

  private retailerId: number;

  private weekId: number;

  constructor(retailerId: number, weekId: number, retailerSku?: string, stacklineSku?: string) {
    this.retailerSku = retailerSku;
    this.stacklineSku = stacklineSku;
    this.retailerId = retailerId;
    this.weekId = weekId;
  }

  public setRetailerSku(retailerSku: string): GetContentMetricsForProductRequestBuilder {
    this.retailerSku = retailerSku;
    return this;
  }

  public setRetailerId(retailerId: number): GetContentMetricsForProductRequestBuilder {
    this.retailerId = retailerId;
    return this;
  }

  public setStacklineSku(stacklineSku: string): GetContentMetricsForProductRequestBuilder {
    this.stacklineSku = stacklineSku;
    return this;
  }

  public setWeekId(weekId: number): GetContentMetricsForProductRequestBuilder {
    this.weekId = weekId;
    return this;
  }

  public clone(): GetContentMetricsForProductRequestBuilder {
    return new GetContentMetricsForProductRequestBuilder(
      this.retailerId,
      this.weekId,
      this.retailerSku,
      this.stacklineSku
    );
  }

  public build() {
    return {
      retailerSku: this.retailerSku,
      retailerId: this.retailerId,
      stacklineSku: this.stacklineSku,
      weekId: this.weekId
    };
  }
}

export class GetWeightedKeywordsAndPhrasesForProductRequestBuilder {
  private retailerSku: string;

  private retailerId: number;

  private weekId: number;

  private contentMetricType: ContentMetricType.KeywordOrganicTraffic | ContentMetricType.KeywordSearchVolume;

  constructor(
    retailerId: number,
    weekId: number,
    retailerSku?: string,
    contentMetricType:
      | ContentMetricType.KeywordOrganicTraffic
      | ContentMetricType.KeywordSearchVolume = ContentMetricType.KeywordOrganicTraffic
  ) {
    this.retailerSku = retailerSku;
    this.retailerId = retailerId;
    this.weekId = weekId;
    this.contentMetricType = contentMetricType;
  }

  public setRetailerSku(retailerSku: string): GetWeightedKeywordsAndPhrasesForProductRequestBuilder {
    this.retailerSku = retailerSku;
    return this;
  }

  public setRetailerId(retailerId: number): GetWeightedKeywordsAndPhrasesForProductRequestBuilder {
    this.retailerId = retailerId;
    return this;
  }

  public setWeekId(weekId: number): GetWeightedKeywordsAndPhrasesForProductRequestBuilder {
    this.weekId = weekId;
    return this;
  }

  public setContentMetricType(
    contentMetricType: ContentMetricType.KeywordOrganicTraffic | ContentMetricType.KeywordSearchVolume
  ): GetWeightedKeywordsAndPhrasesForProductRequestBuilder {
    this.contentMetricType = contentMetricType;
    return this;
  }

  public clone(): GetWeightedKeywordsAndPhrasesForProductRequestBuilder {
    return new GetWeightedKeywordsAndPhrasesForProductRequestBuilder(
      this.retailerId,
      this.weekId,
      this.retailerSku,
      this.contentMetricType
    );
  }

  public build() {
    return {
      retailerSku: this.retailerSku,
      retailerId: this.retailerId,
      weekId: this.weekId,
      contentMetricType: this.contentMetricType
    };
  }
}

/**
 * Builder for creating variations-specific requests.
 * Extends ContentMetricsRequestBuilder with variations-specific fields.
 */
export class VariationsRequestBuilder extends ContentMetricsRequestBuilder {
  private requestType: string;
  private includeMetrics: boolean;

  constructor(
    baseRequest?: BaseMetricsRequest,
    requestType?: string,
    includeMetrics: boolean = true
  ) {
    super(baseRequest);
    this.requestType = requestType;
    this.includeMetrics = includeMetrics;
  }

  public setRequestType(requestType: string): VariationsRequestBuilder {
    this.requestType = requestType;
    return this;
  }

  public setIncludeMetrics(includeMetrics: boolean): VariationsRequestBuilder {
    this.includeMetrics = includeMetrics;
    return this;
  }

  public clone(): VariationsRequestBuilder {
    return new VariationsRequestBuilder(
      _cloneDeep(this.request),
      this.requestType,
      this.includeMetrics
    );
  }

  public build() {
    return {
      ...super.build(),
      requestType: this.requestType,
      includeMetrics: this.includeMetrics
    };
  }
}
